import React, { useState } from 'react';
import { CheckCircle2, Sparkles } from 'lucide-react';
import { useLang } from '@/lib/LangContext';
import ConsultationDialog from '@/components/ConsultationDialog';

const EXPERT_PHOTO = 'https://customer-assets-cm19k8pv.emergentagent.net/job_auto-pet-feeder-1/artifacts/6599d959a07bced1_WhatsApp%20Image%202026-10-09%20at%2016.51.56.jpeg';

/**
 * "Our Experts" section — shown on the home page right after the
 * SmartPaw Feeder spotlight. Highlights Giorgi Kobakhidze and makes it
 * explicit that regular consultations are included with every plan.
 */
export default function OurExpertsSection() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const e = t.experts || {};

  const bullets = [e.bullet1, e.bullet2, e.bullet3, e.bullet4].filter(Boolean);

  return (
    <section id="experts" data-testid="experts-section" className="relative py-14 md:py-20 bg-gradient-to-b from-[#FDFBF7] via-white to-[#FDFBF7]">
      <div className="max-w-6xl mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          {/* Portrait */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[3/4] max-h-[520px] rounded-[2rem] overflow-hidden card-soft bg-[#F5F2EB] mx-auto">
              <img
                src={EXPERT_PHOTO}
                alt={`${e.name} with a dog`}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#05223D]/55 via-[#05223D]/15 to-transparent" aria-hidden />
              <div className="absolute left-4 right-4 bottom-4 flex items-end justify-between gap-3 text-white" data-testid="expert-photo-caption">
                <div>
                  <p className="font-display font-extrabold text-xl md:text-2xl leading-tight drop-shadow">
                    {e.name}
                  </p>
                  <p className="mt-1 text-[11px] md:text-xs font-bold uppercase tracking-[0.18em] text-white/90">
                    {e.role}
                  </p>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-[2rem] bg-[#F25C05]/15 -z-10" aria-hidden />
            <div className="absolute -top-4 -left-4 w-20 h-20 rounded-[2rem] bg-[#0A4D8C]/15 -z-10" aria-hidden />
          </div>

          {/* Content */}
          <div className="lg:col-span-7">
            {e.eyebrow && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#F25C05]/30 text-[11px] font-bold uppercase tracking-wider text-[#F25C05] mb-4">
                <Sparkles size={13} />
                {e.eyebrow}
              </div>
            )}
            <h2 className="font-display font-extrabold text-[#05223D] text-2xl md:text-3xl lg:text-4xl tracking-[-0.02em] leading-[1.15]">
              {e.title}
            </h2>
            <p className="mt-4 text-[#334155] leading-relaxed md:text-lg">
              {e.bio}
            </p>

            {e.benefitsTitle && (
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-[#0A4D8C]">
                {e.benefitsTitle}
              </p>
            )}
            <ul className="mt-3 space-y-2.5">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[#05223D]" data-testid={`expert-bullet-${i}`}>
                  <CheckCircle2 className="w-5 h-5 shrink-0 text-[#0f6e44] mt-0.5" />
                  <span className="text-sm md:text-base leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6">
              <button
                type="button"
                data-testid="experts-cta"
                onClick={() => setOpen(true)}
                className="btn-secondary"
              >
                {e.cta}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConsultationDialog open={open} onOpenChange={setOpen} />
    </section>
  );
}
